import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import DropDown from '../../DropDown';
import HoveredIcon from '../../HoveredIconButton';
import HoveredIconConfirm from '../../HoveredIconConfirmButton';
import DataFieldCard from '../DataFieldCard';
import TaskDependencyCard from './TaskDependencyCard.tsx';
import EmailNotificationCard from '../EmailNotificationCard';
import {
  DataField,
  DataFieldLocationType,
  DataFieldWithOptionsAndSingleLocation,
} from '../../../../../shared/types/DataField';
import {
  TaskTemplate,
  Task,
  TaskWithTaskDependency,
} from '../../../../../shared/types/Task';
import { useDeleteTaskTemplateMutation, useAddTaskDependencyMutation } from '../../../../services/tasksSlice/tasksSlice';
import { AddDataFieldModal } from '../../AddDataFieldModal';
import ModalWidget from '../../../ModalWidget/ModalWidget';
import TaskDependencyModal from '../TaskDependencyModal';
import SpinnerOverlay from '../../../Spinner/Spinner';
import AddTaskNotificationEmail from '../../Others/AddEmailNotifications/AddTaskNotificationEmail';
import { useGetEmailNotificationsQuery } from '../../../../services/emailNotificationsSlice/emailNotificationsSlice';

interface TaskCardProps {
  taskTemplate: TaskTemplate;
  onEditTriggered: (toEdit: TaskTemplate) => void;
  tasks: TaskTemplate[];
  index: number;
}

const calculateTotalProps = (
  dependencies: Task[],
  dataFields: DataFieldWithOptionsAndSingleLocation[],
  emailNotifications?: any[]
) => {
  const dependencyCount = dependencies.length;
  const dataFieldCount = dataFields.length;
  const emailNotificationCount = emailNotifications?.length ?? 0;
  return `${dependencyCount} Dependencies | ${dataFieldCount} Data Fields | ${emailNotificationCount} Email Notifications`;
};

const TaskTemplateCard = ({
  taskTemplate,
  onEditTriggered,
  tasks,
  index,
}: TaskCardProps) => {
  //const preventNestedTask = tasks.filter((task) => task.id !== taskTemplate.id);
  const dependentTasksIDs = taskTemplate?.parentTasks.map(
    (dependency) => dependency.id
  );
  const theme = useTheme();
  const filteredTasks = tasks.filter(
    (task) =>
      task.id !== taskTemplate.id && !dependentTasksIDs.includes(task.id)
  );
  const [deleteTaskTemplate] = useDeleteTaskTemplateMutation();
  const [addTaskDependency, { isLoading: addingTaskDependency }] = useAddTaskDependencyMutation();
  const { data: getEmailNotification, refetch } = useGetEmailNotificationsQuery();
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [handle, setHandle] = useState<string>('');
  const [loadingData, setLoadingData] = useState<boolean>(false);

  const handleDelete = () => {
    deleteTaskTemplate({ id: taskTemplate.id, templateName: taskTemplate.shortName || taskTemplate.name });
  };
    useEffect(() => {
      setLoadingData(addingTaskDependency);
    }, [addingTaskDependency])

  const notificationsData: any = useMemo(
      () =>
        Array.isArray(getEmailNotification)
          ? getEmailNotification.map((n) => ({
              id: n.id !== undefined ? String(n.id) : "",
              subject: n.subject ?? "",
              notificationNotes: n.notificationNotes ?? "",
            }))
          : [],
      [getEmailNotification]
    );

  const openEmailNotificationModal = handle === 'Add Email Notification'; 

  const taskModal =
    handle === 'Add Task Dependency' || handle === 'Edit Task Dependency';   
  return (
    <>
      {(loadingData) && <SpinnerOverlay />}
      {taskModal && (
        <ModalWidget
          isOpen={taskModal}
          title={handle}
          onClose={() => setHandle('')}
          children={
            <>
              <TaskDependencyModal
                taskTemplate={taskTemplate}
                options={filteredTasks}
                description="Choose a task from the dropdown"
                setHandle={setHandle}
                setLoadingData={setLoadingData}
                addTaskDependency={addTaskDependency}
              />
            </>
          }
        />
      )}

      {handle.length ? (
        <AddDataFieldModal
          handle={handle}
          setHandle={setHandle}
          locationID={taskTemplate.id}
          locationType={DataFieldLocationType.Task}
        />
      ) : null}

     {openEmailNotificationModal && (
        <ModalWidget
          isOpen={openEmailNotificationModal}
          onClose={() => setHandle("")}
          title={"Add Notification"}
        >
          <AddTaskNotificationEmail 
          notificationsData={notificationsData}
          setHandle={setHandle}
          taskId={taskTemplate.id}
          />
        </ModalWidget>
      )}

      <Box
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          flexWrap: 'wrap',
          height: !showDetail ? '100px' : '',
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : index % 2 == 0 ? '#F3F8FC' : 'white'
        }}
      >
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >
          <Box alignItems="center">
            <Typography
              component="span"
              sx={{
                fontSize: typographyTokens.fontSizes.base,
                fontWeight: typographyTokens.fontWeights.bold,
              }}
            >
              {taskTemplate.name}  ({taskTemplate?.shortName})
            </Typography>
            &nbsp;&nbsp;
            {(!!taskTemplate.parentTasks.length ||
              !!taskTemplate.taskDataFields.length || !!taskTemplate.tasksNotificationLocation?.length) && (
                <i
                  aria-label="details-toggle"
                  className={`fa-sharp fa-solid fa-chevron-${showDetail ? 'up' : 'down'
                    }`}
                  onClick={() => setShowDetail(!showDetail)}
                ></i>
              )}
          </Box>
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            sx={{
              width: '105px',
            }}
          >
            {/* Delete Button for Task Cards... */}
            <HoveredIconConfirm
              icon="fa-regular fa-trash"
              hoverIcon="fa-solid fa-trash"
              onClick={() => handleDelete()}
            />
            <HoveredIcon
              icon="fa-regular fa-pencil"
              hoverIcon="fa-solid fa-pencil"
              onClick={() => onEditTriggered(taskTemplate)}
            />
            <DropDown
              icon="fa-regular fa-square-plus"
              hoverIcon="fa-solid fa-square-plus"
              items={[
                'Add Task Dependency',
                'Apply Data Field(s)',
                'Add Data Field',
                'Add Email Notification',
              ]}
              onSelect={(e) => setHandle(e)}
            />
          </Stack>
        </Stack>
        <Typography
          component="div"
          sx={{
            fontSize: typographyTokens.fontSizes.xs,
            fontWeight: typographyTokens.fontWeights.bold,
          }}
        >
          {/* {taskTemplate.id && 'Starter Screen   '} */}
          <Typography
            component="span"
            sx={{ fontSize: typographyTokens.fontSizes.xs, color: '#646669' }}
          > Task ID : {taskTemplate.id} |
            {calculateTotalProps(
              taskTemplate.parentTasks,
              taskTemplate.taskDataFields,
              taskTemplate.tasksNotificationLocation
            )}
          </Typography>
        </Typography>
        {/* <Typography
          component="div"
          sx={{
            paddingTop: '15px',
          }}
          dangerouslySetInnerHTML={{
            __html: `<div 
          style="
          display: inline-block;
          max-width:800px;
          white-space: nowrap;
          overflow:hidden;
          text-overflow: ellipsis; "> ${
            !taskTemplate.description
              ? ''
              : taskTemplate.description.replace(/<[^>]+>/g, '')
          }</div>`,
          }}
        >
          {/* { taskTemplate.description ?? '' } */}
        {/* </Typography> */}
      </Box>
      <Divider />
      {showDetail && (
        <CompleteCard
          taskDependencies={taskTemplate.parentTasks}
          taskDataFields={taskTemplate?.taskDataFields}
          taskEmailNotifications={taskTemplate?.tasksNotificationLocation}
          filteredTasks={filteredTasks}
          taskTemplate={taskTemplate}
          setLoadingData={setLoadingData}
        />
      )}
    </>
  );
};

export default TaskTemplateCard;

const CompleteCard = ({
  taskDependencies,
  taskDataFields,
  filteredTasks,
  taskTemplate,
  setLoadingData,
  taskEmailNotifications,
}: any) => {
  const theme = useTheme();
  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f3f5f7',
          padding: '0px 30px 20px 30px',
        }}
      >
        <br />
        {!!taskDependencies.length && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                component="span"
                sx={{
                  color: '#2372B9',
                  font: 'bold 14px/28px Open Sans',
                }}
              >
                Dependencies
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: '#646669',
                  font: 'normal normal normal 12px/28px Open Sans',
                }}
              >
                To begin this task the following dependencies must be done:
              </Typography>
            </Box>


            {/* <Divider /> */}
          </>
        )}
        {taskDependencies.map(
          (taskDependency: TaskWithTaskDependency, index: number) => {
            return (
              <TaskDependencyCard
                key={taskDependency.id}
                taskTemplate={taskTemplate}
                taskDependency={taskDependency}
                index={index}
                lastElement={taskDependencies.length - 1}
                filteredTasks={filteredTasks}
                setLoadingData={setLoadingData}
              />
            );
          }
        )}

        {!!taskDataFields.length  && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: '10px' }}>
              <Typography
                component="span"
                sx={{
                  color: '#2372B9',
                  font: 'bold 14px/28px Open Sans',
                }}
              >
                Data Fields
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: '#646669',
                  font: 'normal normal normal 12px/28px Open Sans',
                }}
              >
                These data fields will appear under this task:
              </Typography>
            </Box>
            {/* <Divider /> */}
          </>
        )}
        {taskDataFields.map((dataField: DataField, index: number) => {

          return (
            <DataFieldCard
              key={dataField.id}
              dataField={dataField}
              dataFields={taskDataFields}
              locationName={taskTemplate?.name}
              locationDescription={taskTemplate?.description}
              index={index}
              lastElement={taskDataFields.length - 1}
            />
          );
        })}
        {!!taskEmailNotifications?.length && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: '10px' }}>
              <Typography
                component="span"
                sx={{
                  color: '#2372B9',
                  font: 'bold 14px/28px Open Sans',
                }}
              >
                Email Notifications
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: '#646669',
                  font: 'normal normal normal 12px/28px Open Sans',
                }}
              >
                These email notifications will be sent for this task:
              </Typography>
            </Box>
          </>
        )}
        {taskEmailNotifications?.map((emailNotification: any, index: number) => {
          return (
            <EmailNotificationCard
              key={emailNotification.id}
              emailNotification={emailNotification}
              index={index}
              lastElement={taskEmailNotifications.length - 1}
              setLoadingData={setLoadingData}
            /> 
          );
        })}
      </Box>
    </>
  );
};
